/**
 * VÉRAULT Travels — Client Profile (Phase 1 of Operational + Experience Pass)
 *
 * The "operational command center" for a single client.  Hybrid layout:
 *   - Sticky compact header (identity + key actions)
 *   - Left sub-nav (anchored, scroll-spy)
 *   - Main scroll content (Identity → Travelers → Trips → Bookings → Money → Invoices → Forms → Notes)
 *   - Right rail on desktop (financial summary + AI assists)
 *   - Stacks intelligently on mobile (sub-nav becomes top scroll-tabs, right rail moves below header)
 *
 * Single backend round-trip via /api/clients/:id/profile.  Two subtle AI assists:
 * "Summarize this client" and "Suggest next action" — both lazy, both cacheable per session.
 *
 * Visual direction: VÉRAULT editorial — serif headings, generous whitespace, calm neutrals.
 * No gradients, no AI-slop visual flourishes.  Designed to feel like a luxury concierge tool.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Mail, Phone, MapPin, Plane, CreditCard, FileText, Users,
  Calendar, Sparkles, AlertCircle, Edit2, ChevronRight,
} from 'lucide-react';
import { formatDate } from '../../utils/dateFormat';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Sections of the profile.  Order = scroll order. */
const SECTIONS = [
  { id: 'identity',  label: 'Identity',  icon: null },
  { id: 'travelers', label: 'Travelers', icon: Users },
  { id: 'trips',     label: 'Trips',     icon: Plane },
  { id: 'bookings',  label: 'Bookings',  icon: Calendar },
  { id: 'money',     label: 'Money',     icon: CreditCard },
  { id: 'invoices',  label: 'Invoices',  icon: FileText },
  { id: 'forms',     label: 'Forms',     icon: FileText },
  { id: 'notes',     label: 'Notes',     icon: null },
];

function money(amount, currency = 'USD') {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency, maximumFractionDigits: 0,
    }).format(Number(amount));
  } catch {
    return `${currency} ${Number(amount).toFixed(0)}`;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Small primitives — kept inline so the page is self-contained
// ─────────────────────────────────────────────────────────────────────────────

function StatusPill({ children, tone = 'neutral' }) {
  const tones = {
    neutral: 'bg-[#F0EDE6] text-[#6B6B6B]',
    success: 'bg-emerald-50 text-emerald-700',
    warning: 'bg-amber-50 text-amber-800',
    danger:  'bg-red-50 text-red-700',
    info:    'bg-blue-50 text-blue-800',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${tones[tone] || tones.neutral}`}>
      {children}
    </span>
  );
}

function paymentTone(status) {
  const s = (status || '').toLowerCase();
  if (s === 'paid') return 'success';
  if (s === 'overdue') return 'danger';
  if (s === 'pending') return 'warning';
  return 'neutral';
}

function SectionHeader({ id, title, accessory }) {
  return (
    <div id={id} className="flex items-end justify-between mb-4 scroll-mt-32">
      <h2 className="font-serif text-2xl text-[#1A1A1A] tracking-tight">{title}</h2>
      {accessory}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AI panels
// ─────────────────────────────────────────────────────────────────────────────

function AISummaryCard({ clientId }) {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.post(`${API}/clients/${clientId}/ai/summarize`, {}, { withCredentials: true });
      setText(res.data.summary || '');
    } catch (e) {
      setError(e.response?.data?.detail || 'AI summary unavailable');
    } finally { setLoading(false); }
  }, [clientId]);

  return (
    <div className="rounded-xl border border-[#E8E4DC] bg-[#FBFAF6] p-4" data-testid="cp-ai-summary">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-[#1A1A1A]">
          <Sparkles className="w-4 h-4 text-[#C8A85A]" />
          <span className="text-xs uppercase tracking-[0.18em] text-[#8C8C8C]">AI Brief</span>
        </div>
        <button
          type="button"
          onClick={run}
          disabled={loading}
          className="text-[11px] uppercase tracking-wider text-[#C8A85A] hover:text-[#A88845] disabled:opacity-50"
          data-testid="cp-ai-summary-run"
        >
          {loading ? 'Thinking…' : (text ? 'Refresh' : 'Summarize')}
        </button>
      </div>
      {error && <p className="text-xs text-red-700">{error}</p>}
      {!text && !loading && !error && (
        <p className="text-xs text-[#8C8C8C] leading-relaxed">
          Three-line operational brief.  Pulls trips, money, forms, and notes — distilled into
          what an advisor needs before the next call.
        </p>
      )}
      {text && (
        <p className="text-sm text-[#1A1A1A] leading-relaxed whitespace-pre-wrap font-serif">
          {text}
        </p>
      )}
    </div>
  );
}

function AIActionsCard({ clientId }) {
  const [actions, setActions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.post(`${API}/clients/${clientId}/ai/next-actions`, {}, { withCredentials: true });
      setActions(res.data.actions || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'AI suggestions unavailable');
    } finally { setLoading(false); }
  }, [clientId]);

  return (
    <div className="rounded-xl border border-[#E8E4DC] bg-[#FBFAF6] p-4" data-testid="cp-ai-actions">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[#1A1A1A]">
          <Sparkles className="w-4 h-4 text-[#C8A85A]" />
          <span className="text-xs uppercase tracking-[0.18em] text-[#8C8C8C]">Next Actions</span>
        </div>
        <button
          type="button"
          onClick={run}
          disabled={loading}
          className="text-[11px] uppercase tracking-wider text-[#C8A85A] hover:text-[#A88845] disabled:opacity-50"
          data-testid="cp-ai-actions-run"
        >
          {loading ? 'Thinking…' : (actions ? 'Refresh' : 'Suggest')}
        </button>
      </div>
      {error && <p className="text-xs text-red-700">{error}</p>}
      {!actions && !loading && !error && (
        <p className="text-xs text-[#8C8C8C] leading-relaxed">
          Ranked next operational steps — sends, follow-ups, and missing data.
        </p>
      )}
      {actions && actions.length === 0 && !loading && (
        <p className="text-xs text-[#8C8C8C]">No actions surfaced. Looking calm.</p>
      )}
      {actions && actions.length > 0 && (
        <ul className="space-y-2.5">
          {actions.map((a, i) => (
            <li key={i} className="flex gap-2.5">
              <span className={`mt-1 inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                a.priority === 'high'   ? 'bg-red-500' :
                a.priority === 'medium' ? 'bg-amber-500' : 'bg-[#999]'
              }`} />
              <div className="min-w-0">
                <p className="text-sm text-[#1A1A1A] leading-tight">{a.label}</p>
                {a.reason && <p className="text-[11px] text-[#8C8C8C] leading-tight mt-0.5">{a.reason}</p>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────

export default function ClientProfilePage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('identity');
  const subnavRef = useRef(null);

  // Fetch the aggregated profile in one round-trip.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const res = await axios.get(`${API}/clients/${clientId}/profile`, { withCredentials: true });
        if (!cancelled) setProfile(res.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Failed to load client');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  // Scroll-spy: highlight the currently-visible section in the sub-nav.
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const visible = entries
        .filter(e => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible[0]) setActiveSection(visible[0].target.id);
    }, { rootMargin: '-120px 0px -60% 0px', threshold: 0 });
    SECTIONS.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [profile]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center text-[#999] text-sm">
        Loading client…
      </div>
    );
  }
  if (error || !profile) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex flex-col items-center justify-center text-sm text-red-700 gap-3">
        <span>{error || 'Client not found'}</span>
        <Button variant="outline" onClick={() => navigate('/advisor/manage/clients')}>Back to clients</Button>
      </div>
    );
  }

  const { client, trips, travelers, bookings, payments, invoices, forms, notes, financials, completeness } = profile;

  const goTo = (id) => () => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="client-profile-page">
      {/* Sticky compact header */}
      <header className="sticky top-0 z-30 bg-[#FAFAF7]/95 backdrop-blur-md border-b border-[#E8E4DC]" data-testid="cp-header">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-4 flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate('/advisor/manage/clients')}
            className="text-[#8C8C8C] hover:text-[#1A1A1A] transition-colors"
            aria-label="Back to clients"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#8C8C8C]">Client Profile</p>
            <h1 className="font-serif text-xl md:text-2xl text-[#1A1A1A] truncate" data-testid="cp-client-name">
              {client.full_name}
            </h1>
          </div>
          <div className="hidden md:flex items-center gap-4 text-xs text-[#6B6B6B]">
            {client.email && (
              <a href={`mailto:${client.email}`} className="flex items-center gap-1.5 hover:text-[#1A1A1A]">
                <Mail className="w-3.5 h-3.5" /> {client.email}
              </a>
            )}
            {client.phone && (
              <a href={`tel:${client.phone}`} className="flex items-center gap-1.5 hover:text-[#1A1A1A]">
                <Phone className="w-3.5 h-3.5" /> {client.phone}
              </a>
            )}
          </div>
          <Link
            to="/advisor/manage/clients"
            className="text-[11px] uppercase tracking-wider text-[#8C8C8C] hover:text-[#1A1A1A] border border-[#E8E4DC] rounded-full px-3 py-1.5"
            data-testid="cp-back-link"
          >
            All clients
          </Link>
        </div>

        {/* Sub-nav scroll-spy */}
        <nav ref={subnavRef} className="max-w-7xl mx-auto px-5 md:px-10 pb-2 overflow-x-auto" data-testid="cp-subnav">
          <ul className="flex gap-1 text-xs">
            {SECTIONS.map(s => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={goTo(s.id)}
                  data-testid={`cp-subnav-${s.id}`}
                  className={`px-3 py-1.5 rounded-full whitespace-nowrap transition-colors ${
                    activeSection === s.id
                      ? 'bg-[#1A1A1A] text-white'
                      : 'text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F0EDE6]'
                  }`}
                >
                  {s.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </header>

      {/* Body — three columns on desktop, single column on mobile */}
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-10">
        {/* MAIN SCROLL CONTENT */}
        <main className="space-y-14 min-w-0">
          {/* Identity */}
          <section>
            <SectionHeader id="identity" title="Identity" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <DetailCard label="Email" value={client.email} icon={Mail} />
              <DetailCard label="Phone" value={client.phone} icon={Phone} />
              <DetailCard label="Home airport" value={client.home_airport} icon={Plane} />
              <DetailCard label="Travel pace" value={client.travel_pace} />
              <DetailCard label="Budget range" value={client.budget_range} />
              <DetailCard label="Dietary needs" value={client.dietary_needs} />
              <DetailCard label="Celebrations" value={client.celebration_dates} />
              <DetailCard label="Status" value={client.status || 'active'} />
            </div>
          </section>

          {/* Travelers */}
          <section>
            <SectionHeader
              id="travelers"
              title="Travelers"
              accessory={<StatusPill>{travelers.length} on file</StatusPill>}
            />
            {travelers.length === 0 ? (
              <EmptyRow message="No travelers linked to this client yet." />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {travelers.map(t => (
                  <div key={t.id} className="rounded-xl border border-[#E8E4DC] bg-white p-4" data-testid={`cp-traveler-${t.id}`}>
                    <p className="text-sm text-[#1A1A1A]">{t.full_name}</p>
                    <p className="text-xs text-[#8C8C8C] mt-0.5">{t.email || '—'}</p>
                    <div className="mt-2 flex items-center gap-2">
                      {t.passport_number
                        ? <StatusPill tone="success">Passport on file</StatusPill>
                        : <StatusPill tone="warning">Passport missing</StatusPill>}
                      {t.date_of_birth && <span className="text-[11px] text-[#8C8C8C]">DOB {t.date_of_birth}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Trips */}
          <section>
            <SectionHeader
              id="trips"
              title="Trips"
              accessory={<StatusPill>{trips.length} total · {completeness.active_trips} active</StatusPill>}
            />
            {trips.length === 0 ? (
              <EmptyRow message="No trips yet." />
            ) : (
              <div className="space-y-2">
                {trips.map(t => (
                  <Link
                    to={`/advisor/trips/${t.id}`}
                    key={t.id}
                    data-testid={`cp-trip-${t.id}`}
                    className="flex items-center gap-4 rounded-xl border border-[#E8E4DC] bg-white p-4 hover:border-[#C8A85A] transition-colors group"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#1A1A1A] truncate">
                        {t.internal_trip_name || t.client_trip_title || t.destinations || 'Untitled trip'}
                      </p>
                      <p className="text-xs text-[#8C8C8C] mt-0.5">
                        {formatDate(t.start_date)} — {formatDate(t.end_date)}
                        {t.num_travelers ? ` · ${t.num_travelers} travelers` : ''}
                      </p>
                    </div>
                    {t.lifecycle_stage && <StatusPill tone="info">{t.lifecycle_stage}</StatusPill>}
                    {t.price_total && <span className="text-sm text-[#1A1A1A] tabular-nums">{money(t.price_total, t.price_currency || financials.currency)}</span>}
                    <ChevronRight className="w-4 h-4 text-[#999] group-hover:text-[#1A1A1A]" />
                  </Link>
                ))}
              </div>
            )}
          </section>

          {/* Bookings */}
          <section>
            <SectionHeader
              id="bookings"
              title="Bookings"
              accessory={<StatusPill>{bookings.length} total</StatusPill>}
            />
            {bookings.length === 0 ? (
              <EmptyRow message="No bookings yet." />
            ) : (
              <div className="space-y-2">
                {bookings.slice(0, 12).map(b => (
                  <div key={b.id} className="flex items-center gap-3 rounded-xl border border-[#E8E4DC] bg-white p-3.5" data-testid={`cp-booking-${b.id}`}>
                    <span className="w-2 h-2 rounded-full bg-[#C8A85A]" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#1A1A1A] truncate">{b.description || b.vendor || b.booking_type}</p>
                      <p className="text-xs text-[#8C8C8C]">{b.booking_type} · {b.vendor || '—'} · {formatDate(b.start_date)}</p>
                    </div>
                    <StatusPill tone={paymentTone(b.payment_status || b.status)}>{b.payment_status || b.status || 'open'}</StatusPill>
                    <span className="text-sm tabular-nums text-[#1A1A1A] w-24 text-right">
                      {money(b.actual_cost || b.quoted_cost, b.currency || financials.currency)}
                    </span>
                  </div>
                ))}
                {bookings.length > 12 && (
                  <p className="text-xs text-[#8C8C8C] text-center pt-2">+ {bookings.length - 12} more</p>
                )}
              </div>
            )}
          </section>

          {/* Money (combined AR + AP) */}
          <section>
            <SectionHeader
              id="money"
              title="Money"
              accessory={
                financials.outstanding_balance > 0
                  ? <StatusPill tone="danger">{money(financials.outstanding_balance, financials.currency)} outstanding</StatusPill>
                  : <StatusPill tone="success">Settled</StatusPill>
              }
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <MoneyTotalsCard title="Receivable · AR" totals={financials.ar} currency={financials.currency} tone="receivable" />
              <MoneyTotalsCard title="Payable · AP"    totals={financials.ap} currency={financials.currency} tone="payable" />
            </div>
            {payments.length === 0 ? (
              <EmptyRow message="No payments tracked yet." />
            ) : (
              <div className="space-y-2">
                {payments.slice(0, 10).map(p => (
                  <div key={p.id} className="flex items-center gap-3 rounded-xl border border-[#E8E4DC] bg-white p-3.5" data-testid={`cp-payment-${p.id}`}>
                    <StatusPill tone={p.direction === 'payable' ? 'warning' : 'info'}>
                      {p.direction === 'payable' ? 'AP' : 'AR'}
                    </StatusPill>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#1A1A1A] truncate">{p.description || p.payment_type || 'Payment'}</p>
                      <p className="text-xs text-[#8C8C8C]">Due {formatDate(p.due_date) || '—'}{p.paid_date ? ` · paid ${formatDate(p.paid_date)}` : ''}</p>
                    </div>
                    <StatusPill tone={paymentTone(p.status)}>{p.status}</StatusPill>
                    <span className="text-sm tabular-nums text-[#1A1A1A] w-24 text-right">
                      {money(p.amount, p.currency || financials.currency)}
                    </span>
                  </div>
                ))}
                {payments.length > 10 && (
                  <p className="text-xs text-[#8C8C8C] text-center pt-2">+ {payments.length - 10} more</p>
                )}
              </div>
            )}
          </section>

          {/* Invoices */}
          <section>
            <SectionHeader
              id="invoices"
              title="Invoices"
              accessory={<StatusPill>{invoices.length} total</StatusPill>}
            />
            {invoices.length === 0 ? (
              <EmptyRow message="No invoices yet." />
            ) : (
              <div className="space-y-2">
                {invoices.map(inv => (
                  <div key={inv.id} className="flex items-center gap-3 rounded-xl border border-[#E8E4DC] bg-white p-3.5" data-testid={`cp-invoice-${inv.id}`}>
                    <span className="text-xs text-[#8C8C8C] w-28">{inv.invoice_number}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#1A1A1A] truncate">{inv.trip_name || '—'}</p>
                      <p className="text-xs text-[#8C8C8C]">Due {formatDate(inv.due_date) || '—'}</p>
                    </div>
                    <StatusPill tone={paymentTone(inv.status)}>{inv.status}</StatusPill>
                    <span className="text-sm tabular-nums text-[#1A1A1A] w-24 text-right">
                      {money(inv.total, inv.currency || financials.currency)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Forms */}
          <section>
            <SectionHeader
              id="forms"
              title="Forms"
              accessory={<StatusPill>{forms.length} total · {completeness.forms_pending} pending</StatusPill>}
            />
            {forms.length === 0 ? (
              <EmptyRow message="No forms sent yet." />
            ) : (
              <div className="space-y-2">
                {forms.slice(0, 10).map(f => (
                  <div key={f.id} className="flex items-center gap-3 rounded-xl border border-[#E8E4DC] bg-white p-3.5" data-testid={`cp-form-${f.id}`}>
                    <FileText className="w-3.5 h-3.5 text-[#C8A85A]" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#1A1A1A] truncate">{f.form_type || 'Form'}</p>
                      <p className="text-xs text-[#8C8C8C]">{formatDate(f.created_at) || '—'}</p>
                    </div>
                    <StatusPill tone={(f.status || '').toLowerCase() === 'completed' ? 'success' : 'warning'}>
                      {f.status}
                    </StatusPill>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Notes */}
          <section>
            <SectionHeader id="notes" title="Notes" />
            {!client.notes && notes.length === 0 ? (
              <EmptyRow message="No notes yet." />
            ) : (
              <div className="space-y-3">
                {client.notes && (
                  <div className="rounded-xl border border-[#E8E4DC] bg-white p-5">
                    <p className="text-[10px] uppercase tracking-wider text-[#8C8C8C] mb-2">Client preferences</p>
                    <p className="text-sm text-[#1A1A1A] leading-relaxed whitespace-pre-wrap">{client.notes}</p>
                  </div>
                )}
                {notes.map(n => (
                  <div key={n.id} className="rounded-xl border border-[#E8E4DC] bg-white p-5" data-testid={`cp-note-${n.id}`}>
                    <p className="text-xs text-[#8C8C8C] mb-2">{formatDate(n.created_at)}</p>
                    <p className="text-sm text-[#1A1A1A] leading-relaxed whitespace-pre-wrap">{n.content || n.text}</p>
                  </div>
                ))}
              </div>
            )}
          </section>
        </main>

        {/* RIGHT RAIL (desktop only) */}
        <aside className="hidden lg:block space-y-4" data-testid="cp-rail">
          <AISummaryCard clientId={clientId} />
          <AIActionsCard clientId={clientId} />

          {/* Quick stats / completeness */}
          <div className="rounded-xl border border-[#E8E4DC] bg-white p-4">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#8C8C8C] mb-3">At a glance</p>
            <ul className="text-xs text-[#1A1A1A] space-y-2">
              <Stat label="Active trips" value={completeness.active_trips} />
              <Stat label="Outstanding (AR)" value={money(financials.outstanding_balance, financials.currency)} tone={financials.outstanding_balance > 0 ? 'danger' : 'neutral'} />
              <Stat label="Forms pending" value={completeness.forms_pending} />
              <Stat label="Travelers" value={`${completeness.travelers_with_passport}/${completeness.travelers_count} with passport`} />
            </ul>
          </div>

          {/* Operational nudges */}
          {(completeness.missing_email || completeness.missing_phone || completeness.missing_home_airport) && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-4 h-4 text-amber-700" />
                <p className="text-[10px] uppercase tracking-[0.18em] text-amber-800">Missing identity info</p>
              </div>
              <ul className="text-xs text-amber-900 space-y-1">
                {completeness.missing_email && <li>· Email not on file</li>}
                {completeness.missing_phone && <li>· Phone not on file</li>}
                {completeness.missing_home_airport && <li>· Home airport not set</li>}
              </ul>
              <Link
                to={`/advisor/manage/clients/${clientId}`}
                className="inline-block mt-3 text-xs underline text-amber-800 hover:text-amber-900"
                data-testid="cp-fix-identity"
              >
                Update identity ↑
              </Link>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Small inline subcomponents
// ─────────────────────────────────────────────────────────────────────────────

function DetailCard({ label, value, icon: Icon }) {
  return (
    <div className="rounded-xl border border-[#E8E4DC] bg-white p-4">
      <div className="flex items-center gap-1.5 mb-1.5">
        {Icon && <Icon className="w-3.5 h-3.5 text-[#C8A85A]" />}
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#8C8C8C]">{label}</p>
      </div>
      <p className="text-sm text-[#1A1A1A] truncate">{value || <span className="text-[#C9C5BB]">—</span>}</p>
    </div>
  );
}

function EmptyRow({ message }) {
  return <p className="text-sm text-[#8C8C8C] italic px-4 py-6 rounded-xl bg-[#F5F3ED]">{message}</p>;
}

function Stat({ label, value, tone = 'neutral' }) {
  return (
    <li className="flex items-baseline justify-between gap-3">
      <span className="text-[#8C8C8C]">{label}</span>
      <span className={`tabular-nums ${tone === 'danger' ? 'text-red-700' : 'text-[#1A1A1A]'}`}>{value}</span>
    </li>
  );
}

function MoneyTotalsCard({ title, totals, currency, tone }) {
  return (
    <div className={`rounded-xl border border-[#E8E4DC] bg-white p-4`}>
      <p className={`text-[10px] uppercase tracking-[0.18em] mb-2 ${tone === 'payable' ? 'text-amber-700' : 'text-blue-700'}`}>{title}</p>
      <p className="font-serif text-xl text-[#1A1A1A] tabular-nums">{money(totals.total, currency)}</p>
      <div className="grid grid-cols-3 gap-2 mt-3 text-[11px]">
        <Mini label="Paid"     value={money(totals.paid, currency)}    tone="success" />
        <Mini label="Pending"  value={money(totals.pending, currency)} tone="warning" />
        <Mini label="Overdue"  value={money(totals.overdue, currency)} tone="danger" />
      </div>
    </div>
  );
}

function Mini({ label, value, tone }) {
  const colors = { success: 'text-emerald-700', warning: 'text-amber-700', danger: 'text-red-700' };
  return (
    <div>
      <p className="text-[9px] uppercase tracking-wider text-[#8C8C8C]">{label}</p>
      <p className={`tabular-nums ${colors[tone]}`}>{value}</p>
    </div>
  );
}
